import type { FC } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';

import { useContentTypesTitleUpdate } from '@confluence/content-types-header/entry-points/useContentTypesTitleUpdate.ts';
import { useUpdateLastEditedTimestampVersionCache } from '@confluence/content-types-utils/entry-points/useUpdateLastEditedTimestampVersionCache.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { KeyboardEventKeys } from '@confluence/keyboard-events';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import {
	useRouteActions,
	useRouteDataRef,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { generateSlug } from '@confluence/route-manager-utils/entry-points/url.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange.ts';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';
import { isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	RedactionsTourSteps,
	RedactionTourStepSource,
	REDACTIONS_TOUR_TARGETS,
	useRedactionsTour,
	useTrackTitleRedactionRendered,
} from '@confluence/redactions/entry-points/tour.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { LivePagesTitlePresentation } from './LivePagesTitlePresentation.tsx';
import { TitleGenerationOnPaste } from './TitleGenerationOnPaste.tsx';
import { MAX_LENGTH_OF_TITLE, AI_SUGGEST_TITLE_PROPS } from './EditorTitle.tsx';
import { isFieldSizingSupported } from './utils/browserSupport.ts';
import { UndoRedoManager } from './utils/undoManager.ts';

const i18nBase = defineMessages({
	placeholder: {
		id: 'editor-title.live-pages-title.placeholder',
		defaultMessage: 'Give this page a title',
		description: 'placeholder prompting user to populate the title field on live pages',
	},
	duplicateTitleError: {
		id: 'editor-title.live-pages-title.duplicate-title-error',
		defaultMessage:
			'There’s already content named “{pageTitle}” in this space. Give this page a unique title.',
		description: 'error to be shown in popup if there is duplicate title of same content type',
	},
	invalidTitleError: {
		id: 'editor-title.live-pages-title.empty-title-error',
		defaultMessage: 'Give your page a unique title so you can find it easily later',
		description: 'error to be shown in popup if the title is empty or comprised of only whitespace',
	},
	generalError: {
		id: 'editor-title.live-pages-title.general-error',
		defaultMessage: 'An error occurred on our end. Refresh and try again.',
		description: 'general error to be shown in popup if the title update request fails',
	},
	aiGeneratedTitleDismissButton: {
		id: 'editor-title.ai.title.error.dismiss',
		defaultMessage: 'Dismiss',
		description:
			'The button dismisses the flag shown when an error occurred trying to generate a title with AI',
	},
	aiGeneratedTitleFromPaste: {
		id: 'editor-title.flags.generate.ai.from.paste.title',
		defaultMessage: 'AI generated a title for this page',
		description:
			'Text of title from a flag that pops up when AI automatically generates a title for user from pasted content',
	},
	aiGeneratedTitleFromPasteDescription: {
		id: 'editor-title.flags.generate.ai.from.paste.description',
		defaultMessage:
			'Rovo generated a title for this page using the content pasted into the editor. <Link>Content quality may vary.</Link>',
		description: 'Description of flag when AI automatically generates a title for user from paste',
	},
});

const i18nLiveDocs = defineMessages({
	placeholder: {
		id: 'editor-title.live-pages-title.placeholder.livedocs',
		defaultMessage: 'Give this live doc a title ',
		description: 'Updated version of editor-title.live-pages-title.placeholder',
	},

	duplicateTitleError: {
		id: 'editor-title.live-pages-title.duplicate-title-error.livedocs',
		defaultMessage:
			'There’s already content named “{pageTitle}” in this space. Give this doc a unique title. ',
		description: 'Updated version of editor-title.live-pages-title.duplicate-title-error',
	},

	invalidTitleError: {
		id: 'editor-title.live-pages-title.empty-title-error.livedocs',
		defaultMessage: 'Give your live doc a unique title so you can find it easily later ',
		description: 'Updated version of editor-title.live-pages-title.empty-title-error',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

export type LivePagesTitleProps = {
	title: string;
	contentId: string;
	cloudId?: string;
	userOffline?: boolean;
	isDisabled?: boolean;
	isEditorEmpty?: boolean;
	focusEditor?: () => void;
	onFocusChanged?: (focusChanged: boolean) => void;
	onTitleBlur?: (currentTitle: string | undefined) => void;
	viewMode?: boolean;
	isTitleCenterAligned?: boolean;
	version?: number;
	editorApi?: FullPageEditorPresetAPI;
	editorSessionId?: string | null;
	isSeamlessTransitionInProgress?: boolean;
	customPlaceholder?: string;
	flags?: FlagsStateContainer;
};

export const LivePagesTitle: FC<LivePagesTitleProps> = ({
	title,
	contentId,
	userOffline,
	isDisabled,
	isEditorEmpty,
	focusEditor,
	onFocusChanged,
	onTitleBlur,
	viewMode,
	isTitleCenterAligned,
	version = 1,
	editorApi,
	editorSessionId,
	isSeamlessTransitionInProgress = false,
	customPlaceholder,
	flags,
}) => {
	const intl = useIntl();
	const { formatMessage } = intl;
	const spaceId = useSpaceId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const hasEditEventFiredRef = useRef<boolean>(false);
	const { updateError, updateTitle, updateLoading } = useContentTypesTitleUpdate(contentId, title);
	const updateTimestampCache = useUpdateLastEditedTimestampVersionCache();
	const { setOptimisticTitle } = useOptimisticTitleChange(contentId, title);
	const [isUserEditing, setUserIsEditing] = useState<boolean>(false);

	// Check if title has changed from the current prop value, gated by experiment
	// The title prop only updates after submission, so it represents the last saved value
	const hasTitleChanged = useCallback(
		(value: string) => {
			if (expValEquals('confluence_live_pages_title_update_diffing', 'isEnabled', true)) {
				return value !== title;
			}
			return true;
		},
		[title],
	);

	const isCSSAutoSizingSupported = isFieldSizingSupported();

	const undoRedoManagerRef = useRef<UndoRedoManager | null>(null);
	const isUndoRedoActionRef = useRef<boolean>(false);

	if (fg('platform_editor_ai_aifc_patch_ga_blockers')) {
		if (undoRedoManagerRef.current === null) {
			undoRedoManagerRef.current = new UndoRedoManager();
		}
	}
	const undoRedoManager = undoRedoManagerRef.current;

	const { replace } = useRouteActions();
	const routeDataRef = useRouteDataRef();

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	useTrackTitleRedactionRendered({ contentId, title });
	const { activeSpotlight } = useRedactionsTour({
		contentId,
		step: RedactionsTourSteps.FIRST,
		firstStepSource: RedactionTourStepSource.titleRedaction,
	});

	const onTitleUpdate = useCallback(
		async (value: string) => {
			setOptimisticTitle(value);
			// We don't want to update the title if the field is blank, otherwise it will update to a default title
			// Also skip if title hasn't actually changed from the last saved value (gated by experiment)
			if (getIsTitleEmpty(value) || !hasTitleChanged(value)) {
				return;
			}

			await updateTitle(value);

			if (undoRedoManager && fg('platform_editor_ai_aifc_patch_ga_blockers')) {
				undoRedoManager.addToUndoStack(value);
			}

			// Update timestamp cache so that Last Edited in object header responsively updates
			if (fg('confluence_frontend_content_wrapper')) {
				updateTimestampCache({
					contentId,
				});
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'updated',
					actionSubject: 'title',
					source: 'editorTitle',
					attributes: { isLivePage: true },
					objectId: contentId,
					objectType: 'page',
					containerType: 'space',
					containerId: spaceId,
				},
			}).fire();

			// We want to fire this event only once to indicate the editor has been edited; the first event measures all title updates.
			if (!hasEditEventFiredRef.current) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'editorTitle',
						action: 'edited',
						actionSubject: 'editor',
						objectId: contentId,
						objectType: 'page',
						attributes: { isLivePage: true, editorSessionId },
						containerType: 'space',
						containerId: spaceId,
					},
				}).fire();
				// Mark the event as fired
				hasEditEventFiredRef.current = true;
			}
		},
		[
			contentId,
			createAnalyticsEvent,
			hasTitleChanged,
			spaceId,
			updateTitle,
			setOptimisticTitle,
			updateTimestampCache,
			editorSessionId,
			undoRedoManager,
		],
	);

	useEffect(() => {
		const contentSlug = generateSlug(title);
		if (
			routeDataRef.current &&
			contentSlug &&
			contentSlug !== routeDataRef.current.routeParams?.contentSlug &&
			routeDataRef.current.routeName === VIEW_PAGE.name
		) {
			// Construct the new URL with the updated slug and existing query params and any hash that was present
			const newUrl = VIEW_PAGE.toUrl(
				{
					...routeDataRef.current.routeParams,
					contentSlug,
				},
				{
					query: {
						...(routeDataRef.current.queryParams || {}),
					},
					hash: routeDataRef.current.hash,
				},
			);

			replace(`${newUrl}`, false);
		}
	}, [title, replace, routeDataRef]);

	const adjustTextAreaHeight = useCallback(
		(textAreaRef: React.RefObject<HTMLTextAreaElement>) => {
			// Skip height adjustment if browser supports field sizing.
			// We only need to adjust the height manually in javascript if it can't be done in the native CSS way.
			if (isCSSAutoSizingSupported) return;

			const textArea = textAreaRef.current;
			if (textArea) {
				// Reset the height in case the new content is smaller than the old content
				textArea.style.height = 'auto';

				// In SSR environment, the previous line does not cause a reflow, so the `scrollHeight` stays zero
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (!process.env.REACT_SSR) {
					// Set the height to match the scroll height, which is the total height of the content
					textArea.style.height = `${textArea.scrollHeight}px`;
				}
			}
		},
		[isCSSAutoSizingSupported],
	);

	const undoAIGeneratedTitle = useCallback(() => {
		const currentTitle = title || '';
		if (!undoRedoManager) {
			return;
		}
		const previousState = undoRedoManager.undo(currentTitle);

		if (currentTitle === previousState) {
			return;
		}

		isUndoRedoActionRef.current = true;
		void onTitleUpdate(previousState);
	}, [title, onTitleUpdate, undoRedoManager]);

	const redoAIGeneratedTitle = useCallback(() => {
		const currentTitle = title || '';
		if (!undoRedoManager) {
			return;
		}
		const nextState = undoRedoManager.redo(currentTitle);

		if (currentTitle === nextState) {
			return;
		}

		isUndoRedoActionRef.current = true;
		void onTitleUpdate(nextState);
	}, [title, onTitleUpdate, undoRedoManager]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// If the user is composing characters using, e.g., the Hiragana - Kana keyboard,
		// short circuit the code to prevent the bug in Chrome where the selected text
		// gets copied into the editor body.
		if (event.nativeEvent.isComposing) {
			return;
		}

		if (undoRedoManager && fg('platform_editor_ai_aifc_patch_ga_blockers')) {
			const isUndoShortcut =
				event.key === 'z' && (isMac() ? event.metaKey : event.ctrlKey) && !event.shiftKey;

			const isRedoShortcut = event.key === 'z' && event.metaKey && event.shiftKey;

			if (isUndoShortcut && undoRedoManager.canUndo()) {
				event.preventDefault();
				event.stopPropagation();
				undoAIGeneratedTitle();
				return;
			}

			if (isRedoShortcut && undoRedoManager.canRedo()) {
				event.preventDefault();
				event.stopPropagation();
				redoAIGeneratedTitle();
				return;
			}
		}

		const isTabPressed = event.key === KeyboardEventKeys.TAB.key && !event.shiftKey;
		// If enter is pressed without publish keyboard shortcut prompt key, prevent default and insert newline in editor body
		const promptKeyForPublishShortcut = isMac() ? event.metaKey : event.ctrlKey;
		const isEnterPressed = event.key === KeyboardEventKeys.ENTER.key;
		const isPublishShortcut = isEnterPressed && promptKeyForPublishShortcut;

		const isDownArrowPressed = event.key === KeyboardEventKeys.DOWN.key;

		if (isTabPressed || isEnterPressed || isDownArrowPressed) {
			event.preventDefault();
			// If the publish keyboard shortcut is pressed, still process it
			if (!isPublishShortcut) {
				event.stopPropagation();
			}

			if (isPublishShortcut || !focusEditor) {
				// if we don't have focusEditor prop or if we're using the publish shortcut, we should call onTitleUpdate
				void onTitleUpdate(event.currentTarget.value);
				setUserIsEditing(false);
			} else if (focusEditor) {
				if (isEnterPressed) {
					editorApi?.core?.actions.execute(
						editorApi?.pageTitleUtils?.commands?.insertNewLineAtDocStart(),
					);
				}
				if (isTabPressed || isDownArrowPressed) {
					editorApi?.core?.actions.execute(editorApi?.pageTitleUtils?.commands?.selectDocStart());
				}
				// if we trigger focusEditor, it will blur the title field.
				// The title is saved on blur, calling onTitleUpdate is avoided here to prevent double trigger.
				focusEditor();
			}
		}
	};

	const handleFocus = () => onFocusChanged?.(true);
	const handleBlur = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
		onTitleBlur?.(e.target.value);
		onFocusChanged?.(false);
		setUserIsEditing(false);
		void onTitleUpdate(e.target.value);
	};

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>, fieldProps: any) => {
		fieldProps.onChange(e);
		adjustTextAreaHeight(textAreaRef);
		setUserIsEditing(true);
	};

	useEffect(() => {
		// To address a timing issue where the height adjustment was occurring before the new title was rendered,
		// a delay  was implemented. Ensures the new title is fully rendered before the text area height is adjusted
		const timeoutId = setTimeout(() => {
			adjustTextAreaHeight(textAreaRef);
		}, 0);
		return () => clearTimeout(timeoutId);
	}, [title, adjustTextAreaHeight, textAreaRef]);

	useLayoutEffect(() => {
		adjustTextAreaHeight(textAreaRef);
	}, [adjustTextAreaHeight, textAreaRef]);

	useEffect(() => {
		if (
			isDisabled ||
			viewMode ||
			version !== 1 ||
			!getIsTitleEmpty(title) ||
			!textAreaRef.current
		) {
			return;
		}

		// When its a new live page with an empty title, focus on the title
		textAreaRef.current.focus();
	}, [isDisabled, title, version, viewMode, textAreaRef]);

	const mountedRef = useRef(false);

	useEffect(() => {
		if (fg('platform_editor_ai_aifc_patch_ga_blockers')) {
			if (textAreaRef.current && mountedRef.current && isUndoRedoActionRef.current) {
				textAreaRef.current.focus();
				isUndoRedoActionRef.current = false;
			}
			mountedRef.current = true;
		}
	}, [title]);

	// Used in onTitleUpdate for checking whether title update should be send to backend
	const getIsTitleEmpty = (title: string | undefined) => !title || title.trim().length === 0;

	// Title is invalid when:
	// 1. Verion is not 1 and it's empty (title was updated to be empty) OR
	// 2. It only contains empty space or tabs
	const getIsInvalidTitle = (title: string | undefined): boolean => {
		return !!((version !== 1 && !title) || (title && title.trim().length === 0));
	};

	const buildErrorMessage = (fieldProps, error) => {
		const updateErrorMessage = updateError?.message.toLowerCase();
		if (fieldProps.isInvalid) {
			return error;
		} else if (getIsInvalidTitle(fieldProps?.value)) {
			return formatMessage(i18n.invalidTitleError);
		} else if (updateErrorMessage?.includes('exists with the same title in this space')) {
			return formatMessage(i18n.duplicateTitleError, {
				pageTitle: fieldProps?.value,
			});
		} else {
			return formatMessage(i18n.generalError);
		}
	};
	// Temporary bugfix: <LivePagesTitlePresentation> has a `key={title}` to force remounting when the title changes.
	// This prevents the title from being outdated when transitioning between live docs when we're not unmounting editor component.
	// The real fix should likely be made on the design system team's <Field> component that's erroronously displaying an outdated title. Their team is looking into this.
	// TODO Remove the `key` when the design system team's <Field> component is fixed.
	return (
		<ErrorBoundary attribution={Attribution.PAGE_MODES}>
			{updateError && <ErrorDisplay error={updateError} />}
			{!isSSR && (
				<TitleGenerationOnPaste
					contentId={contentId}
					textAreaRef={textAreaRef}
					isEditorEmpty={isEditorEmpty}
					userOffline={userOffline ?? false}
					flags={flags}
					i18n={i18n}
					formatMessage={formatMessage}
				/>
			)}
			<SpotlightManager>
				<SpotlightTarget name={REDACTIONS_TOUR_TARGETS.TITLE_REDACTION}>
					<LivePagesTitlePresentation
						{...(fg('confluence_frontend_transition_preloading_system') ? { key: title } : {})}
						title={title}
						isDisabled={isDisabled}
						viewMode={viewMode}
						isTitleCenterAligned={isTitleCenterAligned}
						isUserEditing={isUserEditing}
						updateLoading={updateLoading}
						updateError={updateError}
						handleBlur={handleBlur}
						handleFocus={handleFocus}
						handleChange={handleChange}
						handleKeyDown={handleKeyDown}
						getIsInvalidTitle={getIsInvalidTitle}
						buildErrorMessage={buildErrorMessage}
						textAreaRef={textAreaRef}
						formatMessage={formatMessage}
						i18n={i18n}
						AI_SUGGEST_TITLE_PROPS={AI_SUGGEST_TITLE_PROPS}
						maxLength={MAX_LENGTH_OF_TITLE}
						isCssAutoSizingSupported={isCSSAutoSizingSupported}
						isDisabledTextBlack={isSeamlessTransitionInProgress}
						customPlaceholder={customPlaceholder}
					/>
				</SpotlightTarget>
				<SpotlightTransition>{activeSpotlight}</SpotlightTransition>
			</SpotlightManager>
		</ErrorBoundary>
	);
};
